/* ============================================================================
 * SKIN_BUILDER.EXE — forge a skin
 * ----------------------------------------------------------------------------
 * A skin forged here is DATA. It applies by writing CSS custom properties onto
 * <html>, so a Virtuals agent (or an operator) can create one with no code
 * change and no deploy. That is the whole point of the desk.
 *
 * It may change the palette and three structural switches — density, borders,
 * radius. It may NOT reshape the shell: bar heights, dock size, the bevel, the
 * CRT field and the type stack belong to the master. A fork that could reshape
 * the shell would stop being a fork of one system.
 *
 * Export produces BOTH: a committable .css file for the starter skins we author
 * ourselves, and a portable JSON blob for an agent or a buyer to carry.
 *
 * Personal knobs (accent/ground/density/… in SETTINGS) still stack on top of
 * whatever this desk applies. That layering was settled, not incidental.
 * ========================================================================= */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { appById } from './registry';
import { SkinArt } from '../components/SkinArt';
import {
  ALL_TOKENS,
  FORGE_STORAGE_KEY,
  STRUCTURAL_OPTIONS,
  TOKEN_GROUPS,
  applyForgedSkin,
  blankSkin,
  diffFromMaster,
  jsonToSkin,
  skinToCss,
  skinToJson,
  slug,
  type ForgedSkin,
  type StructuralKnobs,
} from '../lib/skinforge';

function loadForged(): ForgedSkin[] {
  try {
    const raw = localStorage.getItem(FORGE_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as ForgedSkin[]) : [];
  } catch {
    return [];
  }
}

const labelStyle: React.CSSProperties = {
  fontSize: 10,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: 'var(--ui-muted-text)',
  display: 'block',
  marginBottom: 3,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  background: 'var(--ui-well, var(--ui-surface-2))',
  border: '1px solid var(--ui-border)',
  borderRadius: 'var(--ui-radius-ctl)',
  padding: '4px 6px',
  fontSize: 11,
};

function Panel({
  title,
  note,
  children,
}: {
  title: string;
  note?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="panel">
      <span className="eyebrow">{title}</span>
      {note && <p className="note" style={{ marginTop: 4 }}>{note}</p>}
      <div className="stack" style={{ marginTop: 8, gap: 8 }}>
        {children}
      </div>
    </div>
  );
}

export function SkinBuilder() {
  const [draft, setDraft] = useState<ForgedSkin>(() => blankSkin());
  const [saved, setSaved] = useState<ForgedSkin[]>(loadForged);
  const [applied, setApplied] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const importRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    try {
      localStorage.setItem(FORGE_STORAGE_KEY, JSON.stringify(saved));
    } catch {
      /* non-fatal */
    }
  }, [saved]);

  // Leaving the desk drops the forged overlay so the built-in skin returns.
  useEffect(() => () => applyForgedSkin(null), []);

  const patch = useCallback(
    <K extends keyof ForgedSkin>(key: K, value: ForgedSkin[K]) =>
      setDraft((d) => ({ ...d, [key]: value })),
    [],
  );

  const setToken = useCallback((name: string, value: string) => {
    setDraft((d) => {
      const palette = { ...d.palette };
      if (!value || value === ALL_TOKENS.find((t) => t.name === name)?.master) {
        delete palette[name];
      } else {
        palette[name] = value;
      }
      return { ...d, palette };
    });
  }, []);

  const setStructural = useCallback(
    (key: keyof StructuralKnobs, value: string) =>
      setDraft((d) => ({
        ...d,
        structural: { ...d.structural, [key]: value as StructuralKnobs[typeof key] },
      })),
    [],
  );

  const apply = useCallback(() => {
    applyForgedSkin(draft);
    setApplied(draft.id);
    setMsg(`Applied “${draft.label}”.`);
  }, [draft]);

  const revert = useCallback(() => {
    applyForgedSkin(null);
    setApplied(null);
    setMsg('Reverted to the built-in skin.');
  }, []);

  const diff = useMemo(() => diffFromMaster(draft), [draft]);
  const css = useMemo(() => skinToCss(draft), [draft]);
  const json = useMemo(() => skinToJson(draft), [draft]);

  return (
    <div className="stack">
      <div className="dsp page-split">
        <header className="page__head">
          <SkinArt
            className="page__crest"
            src={appById('skin-builder')?.tile ?? '/pixel/dock-skin-builder.png'}
            width={40}
            height={40}
            alt=""
            aria-hidden="true"
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.visibility = 'hidden';
            }}
          />
          <div className="page__titles">
            <p className="eyebrow">skin builder · forge</p>
            <h1 className="h1">Every community gets its own</h1>
          </div>
          <div className="page__chips">
            <span className="badge badge-admin">
              {diff.length} override{diff.length === 1 ? '' : 's'}
            </span>
          </div>
        </header>
      </div>

      <div className="panel">
        <div className="row">
          <span className="row__k">Applied</span>
          <span className="row__v">
            {applied ? (
              <span className="tag tag--ok">{applied}</span>
            ) : (
              <span className="tag tag--muted">built-in skin</span>
            )}
          </span>
        </div>
        <div className="row">
          <span className="row__k">Saved</span>
          <span className="row__v">{saved.length} forged skin(s)</span>
        </div>
        <div className="row">
          <span className="row__k">Differs from master</span>
          <span className="row__v">{diff.length} token(s)</span>
        </div>
      </div>

      {/* ── identity ───────────────────────────────────────────────────── */}
      <Panel
        title="identity"
        note="The name is either bought through us or already the operator's. A forged skin applies at runtime — no commit, no deploy."
      >
        <div>
          <label style={labelStyle} htmlFor="sb-label">Label</label>
          <input
            id="sb-label"
            style={inputStyle}
            value={draft.label}
            onChange={(e) => {
              patch('label', e.target.value);
              patch('id', slug(e.target.value));
            }}
          />
        </div>
        <div>
          <label style={labelStyle} htmlFor="sb-id">id (slug)</label>
          <input
            id="sb-id"
            style={inputStyle}
            value={draft.id}
            onChange={(e) => patch('id', slug(e.target.value))}
          />
        </div>
        <div>
          <label style={labelStyle} htmlFor="sb-wordmark">Wallpaper wordmark</label>
          <input
            id="sb-wordmark"
            style={inputStyle}
            value={draft.wordmark}
            onChange={(e) => patch('wordmark', e.target.value)}
          />
        </div>
        <div>
          <label style={labelStyle} htmlFor="sb-handle">Name / ENS handle</label>
          <input
            id="sb-handle"
            style={inputStyle}
            value={draft.handle}
            onChange={(e) => patch('handle', e.target.value)}
          />
        </div>
        <div className="hstack" style={{ gap: 4 }}>
          {(['issued', 'brought'] as const).map((p) => (
            <button
              key={p}
              type="button"
              className="btn"
              style={{
                fontSize: 10,
                ...(draft.nameProvenance === p
                  ? { borderColor: 'var(--ui-accent)', color: 'var(--ui-accent)' }
                  : {}),
              }}
              onClick={() => patch('nameProvenance', p)}
            >
              {p === 'issued' ? 'bought through us' : "operator's own ENS"}
            </button>
          ))}
        </div>
      </Panel>

      {/* ── structure ──────────────────────────────────────────────────── */}
      <Panel
        title="structure — safe switches only"
        note="Bar heights, dock size, the bevel, the CRT field and the type stack stay with the master. These three are the ones a fork may move."
      >
        {STRUCTURAL_OPTIONS.map((k) => (
          <div key={k.key}>
            <span style={labelStyle}>{k.label}</span>
            <div className="hstack" style={{ gap: 4 }}>
              {k.options.map((o) => (
                <button
                  key={o}
                  type="button"
                  className="btn"
                  style={{
                    fontSize: 10,
                    ...(draft.structural[k.key] === o
                      ? { borderColor: 'var(--ui-accent)', color: 'var(--ui-accent)' }
                      : {}),
                  }}
                  onClick={() => setStructural(k.key, o)}
                >
                  {o}
                </button>
              ))}
            </div>
            <p className="note" style={{ marginTop: 3 }}>{k.note}</p>
          </div>
        ))}
      </Panel>

      {/* ── palette ────────────────────────────────────────────────────── */}
      {TOKEN_GROUPS.map((g) => (
        <Panel key={g.id} title={`palette · ${g.label}`} note={g.note}>
          {g.tokens.map((t) => {
            const value = draft.palette[t.name];
            const overridden = value !== undefined;
            return (
              <div className="hstack" key={t.name} style={{ gap: 6 }}>
                <input
                  type="color"
                  aria-label={`${t.label} colour`}
                  value={overridden ? value : t.master}
                  onChange={(e) => setToken(t.name, e.target.value)}
                  style={{
                    width: 30,
                    height: 22,
                    padding: 0,
                    border: '1px solid var(--ui-border)',
                    borderRadius: 'var(--ui-radius-ctl)',
                    background: 'transparent',
                  }}
                />
                <span style={{ fontSize: 11, minWidth: 108 }}>{t.label}</span>
                <span className="note mono" style={{ flex: '1 1 auto', fontSize: 10 }}>
                  {overridden ? value : `${t.master} (master)`}
                </span>
                {overridden && (
                  <>
                    <span className="tag tag--ok" style={{ fontSize: 8 }}>set</span>
                    <button
                      type="button"
                      className="btn"
                      style={{ fontSize: 9, padding: '1px 5px' }}
                      onClick={() => setToken(t.name, t.master)}
                    >
                      Reset
                    </button>
                  </>
                )}
              </div>
            );
          })}
        </Panel>
      ))}

      {/* ── actions ────────────────────────────────────────────────────── */}
      <div className="hstack">
        <button type="button" className="btn btn--primary" onClick={apply}>
          Apply live
        </button>
        <button type="button" className="btn" onClick={revert} disabled={!applied}>
          Revert
        </button>
        <button
          type="button"
          className="btn"
          onClick={() => {
            setSaved((prev) => {
              const rest = prev.filter((s) => s.id !== draft.id);
              return [...rest, draft];
            });
            setMsg(`Saved “${draft.label}”.`);
          }}
        >
          Save
        </button>
        <button
          type="button"
          className="btn"
          onClick={() => {
            const n = blankSkin('New skin');
            n.id = `${n.id}-${Math.random().toString(36).slice(2, 6)}`;
            setDraft(n);
            setMsg('New blank skin.');
          }}
        >
          New
        </button>
        {applied && (
          <button
            type="button"
            className="btn"
            onClick={() => {
              setSaved((prev) => prev.filter((s) => s.id !== applied));
              revert();
              setMsg('Deleted and reverted.');
            }}
          >
            Delete applied
          </button>
        )}
      </div>

      {msg && <p className="note">{msg}</p>}

      {saved.length > 0 && (
        <>
          <span className="eyebrow">forged skins</span>
          {saved.map((s) => (
            <div className="hstack panel" key={s.id} style={{ gap: 6 }}>
              <span style={{ fontSize: 11 }}>{s.label}</span>
              <span className="tag tag--muted" style={{ fontSize: 8 }}>
                {s.id}
              </span>
              <span className="note" style={{ flex: '1 1 auto', fontSize: 10 }}>
                {diffFromMaster(s).length} override(s) · {s.handle}
              </span>
              <button
                type="button"
                className="btn"
                style={{ fontSize: 9, padding: '1px 6px' }}
                onClick={() => {
                  setDraft(s);
                  applyForgedSkin(s);
                  setApplied(s.id);
                  setMsg(`Applied “${s.label}”.`);
                }}
              >
                Apply
              </button>
              <button
                type="button"
                className="btn"
                style={{ fontSize: 9, padding: '1px 6px' }}
                onClick={() => setDraft(s)}
              >
                Edit
              </button>
            </div>
          ))}
        </>
      )}

      {/* ── export ─────────────────────────────────────────────────────── */}
      <Panel
        title="export — both forms"
        note="The CSS is what we commit for the starter skins we author. The JSON is what an agent or a buyer carries."
      >
        <div className="hstack">
          <button
            type="button"
            className="btn"
            onClick={() => {
              void navigator.clipboard
                ?.writeText(css)
                .then(() => setMsg('CSS copied.'))
                .catch(() => setMsg('Clipboard blocked — select the text below.'));
            }}
          >
            Copy CSS
          </button>
          <button
            type="button"
            className="btn"
            onClick={() => {
              void navigator.clipboard
                ?.writeText(json)
                .then(() => setMsg('JSON copied.'))
                .catch(() => setMsg('Clipboard blocked — select the text below.'));
            }}
          >
            Copy JSON
          </button>
        </div>

        <textarea
          readOnly
          value={css}
          rows={9}
          spellCheck={false}
          style={{ ...inputStyle, fontFamily: 'var(--ui-mono, monospace)', fontSize: 10 }}
        />
        <textarea
          readOnly
          value={json}
          rows={7}
          spellCheck={false}
          style={{ ...inputStyle, fontFamily: 'var(--ui-mono, monospace)', fontSize: 10 }}
        />
      </Panel>

      <Panel title="import" note="Paste a skin payload to load it into the builder.">
        <textarea
          ref={importRef}
          rows={5}
          spellCheck={false}
          placeholder='{"version":1,"skin":{…}}'
          style={{ ...inputStyle, fontFamily: 'var(--ui-mono, monospace)', fontSize: 10 }}
        />
        <div className="hstack">
          <button
            type="button"
            className="btn"
            onClick={() => {
              const text = importRef.current?.value ?? '';
              try {
                const s = jsonToSkin(text);
                setDraft(s);
                setMsg(`Loaded “${s.label}”. Not applied yet.`);
              } catch (e) {
                setMsg(`Not loaded — ${e instanceof Error ? e.message : String(e)}`);
              }
            }}
          >
            Load
          </button>
        </div>
      </Panel>

      <div className="dsp">
        <p className="page__foot">
          A skin is data, not a file. That is what lets an agent mint one without
          a developer, and it is the only reason this desk exists.
        </p>
      </div>
    </div>
  );
}
